import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { 
  Typography, Box, IconButton, Button, 
  List, ListItem, ListItemText, 
  CircularProgress, Menu, MenuItem, Tooltip, useMediaQuery, useTheme,
  Divider, Paper, Tabs, Tab
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AuthContext from "./auth/Context";

import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import PostAddIcon from '@mui/icons-material/PostAdd'; 
import AddIcon from '@mui/icons-material/Add';
import CodeIcon from '@mui/icons-material/Code';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import TerminalIcon from '@mui/icons-material/Terminal';
import ManagedModal from './ManagedModal';
import DataListForm from './DataListForm';
import {
  buildRptFormFields, boFieldGiaoDien, layIdFile, taoDuongDanFile, doanLoaiMau, laScriptExcel,
  chuanHoaHuongDanExcel, TEN_FILE_HUONG_DAN_EXCEL,
} from './rptTemplateFormConfig';
import { listinfoHeaders } from './listinfoBinding';

export default function DataIOActionDrawer({ 
  open, 
  onClose, 
  apiCode,
  code,             
  // Mã listinfo mà màn hình GỌI ĐẾN đang mượn hành vi server (xem listinfoBinding.js). Không truyền thì
  // lùi về `code` — giữ nguyên hành vi cũ.
  listinfoCode,
  mode = 'report',     
  searchCondition,     
  selectedIds = [],    
  title = 'Báo cáo',   
  columns = [],
  filterFields = [],
  onImportSuccess 
}) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  
  const context = useContext(AuthContext);
  const userToken = context.userInfo?.token || (typeof context.getToken === 'function' ? context.getToken() : '');

  const [isProcessing, setIsProcessing] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [printTypeAction, setPrintTypeAction] = useState('print');
  
  // Quản lý Tab (Mặc định là export)
  const [activeTab, setActiveTab] = useState('export');

  const [drawerId] = useState(() => 'io_' + Math.random().toString(36).slice(2, 7));

  // Thêm/sửa/xoá MẪU IN ngay trong drawer (trước đây phải mở màn hình quản lý mẫu ở tab khác)
  const queryClient = useQueryClient();
  const [templateForm, setTemplateForm] = useState({ open: false, editing: false, data: null });
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Refs cho input chọn file (Chỉ dùng khi mode != 'report')
  const excelFileInputRef = useRef(null);
  const jsonFileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      const base = window.location.pathname + window.location.search;
      const [hashPath = '', hashSearch = ''] = window.location.hash.split('?');
      const searchParams = new URLSearchParams(hashSearch);
      searchParams.set(drawerId, "open");
      window.history.pushState(null, '', base + `${hashPath}?${searchParams.toString()}`);
      
      // Reset tab về mặc định mỗi khi mở lại
      setActiveTab('export');
    }
  }, [open, drawerId]);

  const checkUrl = useCallback(() => {
    if (open) {
      const [, hashSearch = ''] = window.location.hash.split('?');
      const searchParams = new URLSearchParams(hashSearch);
      if (searchParams.get(drawerId) !== "open") {
         if (onClose) onClose();
      }
    }
  }, [open, drawerId, onClose]);

  useEffect(() => {
    window.addEventListener('popstate', checkUrl);
    return () => window.removeEventListener('popstate', checkUrl);
  }, [checkUrl]);

  const handleUserClose = () => {
    if (isProcessing) return; 
    window.history.back(); 
  };

  const { data: templates, isLoading } = useQuery({
    queryKey: ['print_templates', code],
    queryFn: async () => {
      const res = await context.apis.asyncGetList(userToken, 'rpt', { condition: { ma_cn: code }, limit: 100 });
      return res?.data || res || [];
    },
    enabled: open && !!code && !!userToken,
  });

  // Cấu hình LISTINFO CŨ của `rpt` là nguồn khai báo field cho form thêm/sửa mẫu (nhãn, bắt buộc,
  // field công ty tự thêm). Không đọc được thì rptTemplateFormConfig dùng bản mặc định.
  const { data: rptListinfo } = useQuery({
    queryKey: ['listinfo', 'rpt'],
    queryFn: async () => await context.apis.asyncGetData(userToken, 'listinfo', { code: 'rpt' }, null, true, true),
    enabled: open && !!userToken,
    staleTime: 30 * 60 * 1000,
  });

  const idApp = context.config?.id_app || context.appInfo?._id || '';
  const urlHuongDan = `${context.config?.server_url || ''}/docs/${TEN_FILE_HUONG_DAN_EXCEL}`;

  // Hướng dẫn viết script excel ĐỌC TỪ MÁY CHỦ (route tĩnh /docs) thay vì nhúng cứng trong app:
  // sửa tài liệu ở backend là mọi client thấy ngay. Tải hỏng -> buildRptFormFields tự dùng bản dự phòng.
  const { data: huongDanExcel } = useQuery({
    queryKey: ['docs', TEN_FILE_HUONG_DAN_EXCEL],
    queryFn: async () => {
      const res = await fetch(urlHuongDan);
      if (!res.ok) throw new Error(`Không tải được tài liệu (${res.status})`);
      return chuanHoaHuongDanExcel(await res.text());
    },
    enabled: open && mode === 'report' && !!context.config?.server_url,
    staleTime: 60 * 60 * 1000,
    retry: 0,
  });

  const rptFormFields = React.useMemo(() => buildRptFormFields({
    listinfoFields: rptListinfo?.fields,
    code,
    isReport: mode === 'report',
    columns,
    tieuDe: title,
    huongDanExcel,
  }), [rptListinfo, code, mode, columns, title, huongDanExcel]);

  // Mẫu chỉ có script (không file, không html) CHỈ chạy được cho báo cáo — danh mục/chứng từ đi qua
  // exportHandler và bắt buộc phải có file mẫu.
  const xuatBangScript = (tpl) => mode === 'report' && laScriptExcel(tpl.handle_data_expression);
  const coTheXuatFile = (tpl) => !!tpl.file_mau_in || xuatBangScript(tpl);

  // Một mẫu có thể vừa in HTML vừa xuất file — mô tả phải nói đủ, đừng bỏ sót khả năng nào.
  const moTaMau = (tpl) => {
    const kn = [];
    if (tpl.html_template) kn.push('In HTML');
    if (tpl.file_mau_in) kn.push('Xuất theo file mẫu');
    if (xuatBangScript(tpl)) kn.push('Xuất Excel bằng script');
    if (kn.length) return kn.join('  ·  ');
    if (tpl.handle_data_expression && mode !== 'report') return 'Thiếu file mẫu — chưa xuất được';
    return 'Chưa khai nội dung mẫu';
  };

  const lamMoiDanhSachMau = () => queryClient.invalidateQueries({ queryKey: ['print_templates', code] });

  const moFormThemMau = () => setTemplateForm({
    open: true, editing: false,
    data: { ma_cn: String(code || '').toUpperCase(), ten_mau_in: '', _loai_mau: '', _nang_cao: false },
  });

  const moFormSuaMau = (tpl) => setTemplateForm({
    open: true, editing: true,
    data: {
      ...tpl,
      file_mau_in: layIdFile(tpl.file_mau_in),
      _loai_mau: doanLoaiMau(tpl),
      _nang_cao: !!(tpl.send_to || tpl.bluetooth_template || (tpl.cac_lien || []).length || (tpl.parameters || []).length),
    },
  });

  const luuMau = async (formData) => {
    const loai = formData._loai_mau;
    const payload = boFieldGiaoDien(formData);

    // KHÔNG xoá nội dung của loại khác: nhiều mẫu cũ vừa có html_template vừa có file_mau_in
    // (in trực tiếp + xuất file). "Loại mẫu" chỉ quyết định bạn đang soạn phần nào.
    payload.id_app = payload.id_app || idApp;
    payload.ma_cn = String(payload.ma_cn || code || '').toUpperCase();
    payload.file_mau_in = payload.file_mau_in ? taoDuongDanFile(payload.file_mau_in, idApp) : '';

    // Chặn sớm hai lỗi server sẽ báo bằng tiếng máy: có `async`, hoặc script không trả cấu hình.
    const script = payload.handle_data_expression || '';
    if (script.indexOf('async ') >= 0) {
      context.alert("Script không được chứa từ khoá 'async' (hệ thống chặn). Hãy bỏ 'async', dùng 'await' trực tiếp nếu cần.", null, 'red');
      return;
    }
    if (loai === 'excel_config' && script.indexOf('return') < 0) {
      context.alert('Script phải kết thúc bằng lệnh return trả về cấu hình, ví dụ: return { sheets: [ ... ] };', null, 'red');
      return;
    }

    setSavingTemplate(true);
    try {
      // Lưu MẪU IN: luôn theo listinfo `rpt` (chính nó là loại chứng từ ở đây), không phải listinfo
      // của màn hình đang mở.
      const rs = await context.apis.asyncPostList(userToken, 'rpt', payload, { headers: listinfoHeaders({ moduleCode: 'rpt' }) });
      if (rs && rs.error) throw new Error(rs.error);
      setTemplateForm({ open: false, editing: false, data: null });
      await lamMoiDanhSachMau();
      context.alert(templateForm.editing ? 'Đã cập nhật mẫu.' : 'Đã tạo mẫu mới.', null, 'green');
    } catch (e) {
      context.alert(e.message || 'Lưu mẫu thất bại!', null, 'red');
    } finally { setSavingTemplate(false); }
  };

  const xoaMau = (tpl) => {
    context.alert(`Xoá mẫu "${tpl.ten_mau_in}"? Thao tác này không khôi phục được.`, async (yes) => {
      if (!yes) return;
      try {
        const rs = await context.apis.asyncDeleteList(userToken, 'rpt', tpl._id);
        if (rs && rs.error) throw new Error(rs.error);
        await lamMoiDanhSachMau();
        context.alert('Đã xoá mẫu.', null, 'green');
      } catch (e) { context.alert(e.message || 'Xoá mẫu thất bại!', null, 'red'); }
    }, 'warning');
  };

  // ==========================================
  // HÀM XỬ LÝ IMPORT DỮ LIỆU
  // ==========================================
  const importData = (options_import, file_type = "excel") => {
    if (mode === 'report') return; 

    let file = options_import.file;
    let url;
    
    if(context.apis?.list_not_require_id_app?.indexOf(apiCode) >= 0){
      url = context.config.server_url + "/api/" + apiCode + `/import/${file_type}?access_token=` + (typeof context.getToken === 'function' ? context.getToken() : userToken);
    } else {
      url = context.config.server_url + "/api/" + context.appInfo?._id + "/" + apiCode + `/import/${file_type}?access_token=` + (typeof context.getToken === 'function' ? context.getToken() : userToken);
    }
    
    url = `${url}&listinfo_code=${code}`;
    
    for(let key in options_import){
      if(key !== "access_token" && key !== "file" && options_import[key] != undefined && options_import[key] != null){
        url = `${url}&${key}=${encodeURIComponent(options_import[key])}`;
      }
    }
    
    setIsProcessing(true);
    context.apis.uploadFile(url, file, (error, rs) => {
      setIsProcessing(false);
      if(error){
        console.error(error);
        return context.alert(error.message || error.error || error || context.apis?.getLabel("Đã có lỗi khi import dữ liệu") || "Có lỗi xảy ra", null, "red");
      } 
      
      try { rs = JSON.parse(rs); } catch(e) {}
      
      if(rs && rs.message){
        context.alert(context.apis?.getLabel(rs.message) || rs.message, null, "green");
      } else {
        context.alert(context.apis?.getLabel("Chương trình đã thực hiện xong") || "Thực hiện thành công", null, "green");
      }
      
      if (onImportSuccess) onImportSuccess();
      
    }, { name: file_type === "excel" ? "xlsx" : "json" });
  };

  const handleFileChange = (event, type) => {
    const file = event.target.files[0];
    if (!file) return;
    importData({ file }, type);
    event.target.value = null; 
  };

  // ==========================================
  // HÀM XỬ LÝ EXPORT & PRINT
  // ==========================================
  const executePrint = async (template, type, maLien = null, type_data="xlsx", return_type="url") => {
    //console.log("[executePrint]",{mode,template, type,type_data});
    setIsProcessing(true);
    try {
      const payloadTemplate = { ...template, lien_duoc_chon: maLien };
      let response;
      
      const headers = listinfoHeaders({ listinfoCode, moduleCode: code });
      if (mode === 'report') {
        response = await context.apis.asyncDownloadReportWithTemplate(userToken, apiCode, payloadTemplate, searchCondition, type, code,type_data,return_type);
      } else {
        if(template._id === "default"){
            response = await context.apis.asyncGetList(userToken, apiCode, {template:payloadTemplate, type_data,condition:searchCondition,headers});
        }else{
            if (selectedIds.length === 0) { 
                context.alert("Vui lòng chọn ít nhất 1 dòng dữ liệu để in!", null, "orange"); 
                return; 
            }
            response = await context.apis.asyncDownloadVoucherWithTemplate(userToken, apiCode, selectedIds.join(','), payloadTemplate, type, code);
        }
      }

      if (response && response.url) {
          context.apis.downloadFile(response.url, (template.file||{}).name || template?.ten_mau_in, (e) => {
            if(e) context.alert(e.message||e.error||e);
          });
      } else if (response && response.html) {
          const iframe = document.createElement('iframe');
          iframe.style.position = 'fixed'; iframe.style.right = '0'; iframe.style.bottom = '0'; iframe.style.width = '0'; iframe.style.height = '0'; iframe.style.border = '0';
          document.body.appendChild(iframe);
          const iframeDoc = iframe.contentWindow.document;
          iframeDoc.open(); iframeDoc.write(response.html); iframeDoc.close();
          setTimeout(() => {
              iframe.contentWindow.focus(); iframe.contentWindow.print();
              setTimeout(() => { if (document.body.contains(iframe)) document.body.removeChild(iframe); }, 1000); 
          }, 500); 
      }
    } catch (error) { 
        console.error(error); 
        context.alert("Có lỗi xảy ra trong quá trình xuất dữ liệu.", null, "red"); 
    } finally { setIsProcessing(false); setAnchorEl(null); }
  };

  const handleActionClick = (event, template, type) => {
    // cac_lien từ API là MẢNG; bản ghi cũ có thể lưu chuỗi JSON -> nhận cả hai, nếu không thì
    // menu chọn liên không bao giờ hiện và mẫu nhiều liên chỉ in được 1 liên.
    let danhSachLien = [];
    try {
      danhSachLien = Array.isArray(template.cac_lien)
        ? template.cac_lien
        : (template.cac_lien ? JSON.parse(template.cac_lien) : []);
    } catch (e) { danhSachLien = []; }
    if (danhSachLien && danhSachLien.length > 0) {
        setSelectedTemplate({ template, danhSachLien });
        setPrintTypeAction(type);
        setAnchorEl(event.currentTarget);
    } else {
        executePrint(template, type);
    }
  };

  const executeDefaultExcel = () => {
    const finalCondition = { ...searchCondition }
    const virtualTemplate = {
      _id: "default",
      title: title || 'Báo cáo',
      ten_mau_in: `${title}.xlsx`,
      condition: finalCondition,
      columns: columns.map(c => ({
        field: c.field,
        label: c.label || c.field,
        type: c.type,
        source_field: c.source_field, // NEW: field gốc của cột Pivot (nếu có), để server đối chiếu rptInfo
      })),
      filterFields: filterFields.map(f => ({ field: f.field, label: f.label, type: f.type, mapTo: f.mapTo }))
    };
    executePrint(virtualTemplate, 'file');
  };

  const executeDefaultJSON = () => {
    const finalCondition ={ ...searchCondition }
    const virtualTemplate = {
      _id: "default",
      title: title || 'Báo cáo',
      ten_mau_in:`${title}.json`,
      condition: finalCondition
    };
    executePrint(virtualTemplate, 'file', null, "json_file");
  };

  const executeDefaultPrint = async () => {
      setIsProcessing(true);
      try {
          let payload = { ...searchCondition, limit: 100000, pagination: 0 }; 
          let dataList = [];
          
          if (mode === 'report') {
              const res = await context.apis.asyncGetReport(userToken, apiCode, payload);
              dataList = res?.data || res || [];
          } else {
              const res = await context.apis.asyncGetList(userToken, apiCode, { condition: { _id: { $in: selectedIds } }, limit: selectedIds.length });
              dataList = res?.data || res || [];
          }

          if (dataList.length === 0) { context.alert("Không có dữ liệu để in.", null, "orange"); return; }

          const filterTexts = filterFields.map(fieldDef => {
            if (fieldDef.type === 'DateFilter') {
                const fromKey = fieldDef.mapTo?.[0] || 'fromDate';
                const toKey = fieldDef.mapTo?.[1] || 'toDate';
                const fromVal = searchCondition?.[fromKey];
                const toVal = searchCondition?.[toKey];
                if (!fromVal && !toVal) return null;
                const formatVN = (dStr) => {
                    if (!dStr) return '...';
                    const p = dStr.split('-');
                    if (p.length === 3) return `${p[2]}/${p[1]}/${p[0]}`;
                    return dStr;
                };
                return `${fieldDef.label}: Từ ${formatVN(fromVal)} đến ${formatVN(toVal)}`;
            } else {
                const val = searchCondition?.[fieldDef.field];
                if (val === undefined || val === null || val === '') return null;
                if (Array.isArray(val) && val.length === 0) return null;
                if (fieldDef.type === 'Boolean') return `${fieldDef.label}: ${val ? 'Có' : 'Không'}`;

                let displayVal = val;
                if (Array.isArray(val)) displayVal = val.join(', ');
                else if (typeof val === 'object') displayVal = JSON.stringify(val);
                return `${fieldDef.label}: ${displayVal}`;
            }
          }).filter(Boolean).join(' | ');

          const renderVal = (val, colType) => {
              if (val === null || val === undefined) return '';
              if (colType === 'Boolean') return val ? 'Có' : 'Không';
              if (typeof val === 'object') return JSON.stringify(val);
              if (colType === 'Number') return new Intl.NumberFormat('vi-VN').format(val);
              return val;
          };

          const htmlContent = `
            <div style="font-family: Arial, sans-serif; padding: 20px;">
                <h2 style="text-align: center; color: #333; margin-bottom: 5px;">${title}</h2>
                ${filterTexts ? `<p style="text-align: center; color: #555; font-size: 13px; margin-top: 0;"><em>${filterTexts}</em></p>` : ''}
                <table style="width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 14px;" border="1" cellpadding="8">
                    <thead>
                        <tr style="background-color: #f5f5f5; color: #333;">
                            <th style="border: 1px solid #ddd; width: 50px;">STT</th>
                            ${columns.map(c => `<th style="border: 1px solid #ddd;">${c.label || c.field}</th>`).join('')}
                        </tr>
                    </thead>
                    <tbody>
                        ${dataList.map((row, idx) => `<tr><td style="border: 1px solid #ddd; text-align: center;">${idx + 1}</td>${columns.map(c => `<td style="border: 1px solid #ddd; ${c.type === 'Number' ? 'text-align: right;' : ''}">${renderVal(row[c.field], c.type)}</td>`).join('')}</tr>`).join('')}
                    </tbody>
                </table>
            </div>
          `;

          const iframe = document.createElement('iframe');
          iframe.style.position = 'fixed'; iframe.style.right = '0'; iframe.style.bottom = '0'; iframe.style.width = '0'; iframe.style.height = '0'; iframe.style.border = '0';
          document.body.appendChild(iframe);
          const iframeDoc = iframe.contentWindow.document;
          iframeDoc.open(); iframeDoc.write(htmlContent); iframeDoc.close();

          setTimeout(() => {
              iframe.contentWindow.focus(); iframe.contentWindow.print();
              setTimeout(() => { if (document.body.contains(iframe)) document.body.removeChild(iframe); }, 1000); 
          }, 500); 
      } catch (error) {
          console.error(error); context.alert("Lỗi tải dữ liệu in.", null, "red");
      } finally { setIsProcessing(false); }
  };

  const handleOpenTemplateManager = () => {
    window.open(`/#/page/list/rpt?ma_cn=${apiCode}`, '_blank');
  };

  return (
    <>
      {/* Input file ẩn dùng chung */}
      {mode !== 'report' && (
        <>
          <input type="file" accept=".xlsx, .xls" style={{ display: 'none' }} ref={excelFileInputRef} onChange={(e) => handleFileChange(e, 'excel')} />
          <input type="file" accept=".json" style={{ display: 'none' }} ref={jsonFileInputRef} onChange={(e) => handleFileChange(e, 'json')} />
        </>
      )}

      <ManagedModal
        type="drawer"
        anchor={isDesktop ? "right" : "bottom"} open={open} onClose={handleUserClose} 
        PaperProps={{ sx: isDesktop ? 
            { width: 450, borderRadius: 3, m: 2, height: 'auto', maxHeight: 'calc(100dvh - 32px)', display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: 'background.default' } : 
            { borderTopLeftRadius: 24, borderTopRightRadius: 24, pb: 'max(24px, env(safe-area-inset-bottom))', maxHeight: '90vh', height: '90vh', width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: 'background.default' } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            
            {/* --- HEADER --- */}
            <Box sx={{ px: 2, pt: 2, flexShrink: 0 }}>
                {!isDesktop && (<Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}><Box sx={{ width: 40, height: 4, bgcolor: 'divider', borderRadius: 2 }} /></Box>)}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" fontWeight="bold" color="primary.main" sx={{ display: 'flex', alignItems: 'center' }}>
                    {mode !== 'report' ? (
                      <><ImportExportIcon sx={{ mr: 1 }} /> Nhập / Xuất dữ liệu</>
                    ) : (
                      <><PrintIcon sx={{ mr: 1 }} /> Tùy chọn In & Xuất file</>
                    )}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Thêm mẫu in/xuất mới">
                      <IconButton onClick={moFormThemMau} disabled={isProcessing} size="small" sx={{ bgcolor: 'action.hover' }}>
                        <AddIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Mở màn hình quản lý mẫu (nâng cao)">
                      <IconButton onClick={handleOpenTemplateManager} disabled={isProcessing} size="small" sx={{ bgcolor: 'action.hover' }}>
                        <SettingsIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                    <IconButton onClick={handleUserClose} disabled={isProcessing} size="small" sx={{ bgcolor: 'action.hover' }}><CloseIcon sx={{ fontSize: 20 }} /></IconButton>
                  </Box>
                </Box>
            </Box>

            {/* --- TABS BÀI TOÁN --- */}
            {mode !== 'report' && (
              <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, flexShrink: 0 }}>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} variant="fullWidth" indicatorColor="primary" textColor="primary">
                  <Tab label="Xuất / In ấn" value="export" sx={{ textTransform: 'none', fontWeight: 'bold', fontSize: '0.95rem' }} />
                  <Tab label="Nhập dữ liệu" value="import" sx={{ textTransform: 'none', fontWeight: 'bold', fontSize: '0.95rem' }} />
                </Tabs>
              </Box>
            )}
            
            {/* --- KHU VỰC NỘI DUNG CÓ THỂ CUỘN LÊN/XUỐNG --- */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0, px: 2, py: 1 }}>
              
              {/* TAB 1: EXPORT (HOẶC MẶC ĐỊNH CHO REPORT) */}
              {activeTab === 'export' && (
                <>
                  {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={30} /></Box>
                  ) : (
                    <List sx={{ pt: 0, pb: 2 }}>
                      
                      {(!templates || templates.length === 0) && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: 3 }}>
                           <PostAddIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                           <Typography variant="body2" fontWeight="bold" color="text.secondary" gutterBottom>
                             Chưa có mẫu in/xuất nào
                           </Typography>
                           <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={moFormThemMau} sx={{ borderRadius: 2, textTransform: 'none', boxShadow: 'none' }}>
                             Tạo mẫu mới ngay
                           </Button>
                           {mode === 'report' && (
                             <Typography variant="caption" color="text.secondary" sx={{ mt: 1, maxWidth: 320 }}>
                               Mẹo: chọn loại <b>Excel bằng script</b> — hệ thống tự sinh sẵn khung theo các cột bạn đang xem, không cần thiết kế file mẫu.
                             </Typography>
                           )}
                        </Box>
                      )}

                      {templates?.map((tpl, index) => (
                        <ListItem key={tpl._id || index} sx={{ py: 2, px: 0, flexDirection: 'column', alignItems: 'flex-start', borderBottom: '1px dashed #e0e0e0' }}>
                          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <ListItemText
                              primary={<Typography fontWeight="bold" sx={{ color: '#1c1c1e' }}>{tpl.ten_mau_in}</Typography>}
                              secondary={<Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {!tpl.html_template && !tpl.file_mau_in && tpl.handle_data_expression && <TerminalIcon sx={{ fontSize: 14 }} />}
                                {moTaMau(tpl)}
                              </Box>}
                            />
                            <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                              <Tooltip title="Sửa mẫu">
                                <IconButton size="small" disabled={isProcessing} onClick={() => moFormSuaMau(tpl)}><EditIcon sx={{ fontSize: 18 }} /></IconButton>
                              </Tooltip>
                              <Tooltip title="Xoá mẫu">
                                <IconButton size="small" color="error" disabled={isProcessing} onClick={() => xoaMau(tpl)}><DeleteOutlineIcon sx={{ fontSize: 18 }} /></IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
                            {tpl.html_template && (
                              <Button variant="contained" color="primary" size="small" fullWidth disabled={isProcessing} startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />} onClick={(e) => handleActionClick(e, tpl, 'print')} sx={{ textTransform: 'none', borderRadius: 2, boxShadow: 'none' }}>In trực tiếp</Button>
                            )}
                            {coTheXuatFile(tpl) && (
                              <Button variant="outlined" color="success" size="small" fullWidth disabled={isProcessing} startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />} onClick={(e) => handleActionClick(e, tpl, 'file')} sx={{ textTransform: 'none', borderRadius: 2 }}>Xuất file</Button>
                            )}
                          </Box>
                        </ListItem>
                      ))}

                      {templates && templates.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', alignItems: 'center', pt: 1.5 }}>
                          <Button size="small" startIcon={<AddIcon />} onClick={moFormThemMau} disabled={isProcessing} sx={{ textTransform: 'none', borderRadius: 2 }}>
                            Thêm mẫu
                          </Button>
                          {mode === 'report' && (
                            <Button size="small" color="inherit" startIcon={<MenuBookIcon />} href={urlHuongDan} target="_blank" rel="noopener" sx={{ textTransform: 'none', borderRadius: 2, color: 'text.secondary' }}>
                              Hướng dẫn tạo mẫu
                            </Button>
                          )}
                        </Box>
                      )}

                      {templates && templates.length > 0 && <Divider sx={{ my: 1 }} />}

                      {/* NÚT XUẤT MẶC ĐỊNH */}
                      <ListItem sx={{ py: 2, px: 0, flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <ListItemText primary={<Typography fontWeight="bold" sx={{ color: '#1c1c1e', display: 'flex', alignItems: 'center', gap: 0.5 }}><DescriptionIcon fontSize="small" color="primary"/> Xuất dữ liệu mặc định</Typography>} secondary="Xuất theo cột dữ liệu đang hiển thị" />
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 1, width: '100%', flexWrap: 'wrap' }}>
                            {mode === "report" && (
                              <Button variant="contained" color="primary" size="small" sx={{ flex: 1, minWidth: '100px', textTransform: 'none', borderRadius: 2, boxShadow: 'none', bgcolor: '#e3f2fd', color: 'primary.main', '&:hover': { bgcolor: '#bbdefb' } }} disabled={isProcessing} startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />} onClick={executeDefaultPrint}>
                                In nhanh
                              </Button>
                            )}
                            <Button variant="outlined" color="success" size="small" sx={{ flex: 1, minWidth: '100px', textTransform: 'none', borderRadius: 2 }} disabled={isProcessing} startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />} onClick={executeDefaultExcel}>
                              Tải Excel
                            </Button>
                            <Button variant="outlined" color="info" size="small" sx={{ flex: 1, minWidth: '100px', textTransform: 'none', borderRadius: 2 }} disabled={isProcessing} startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : <CodeIcon />} onClick={executeDefaultJSON}>
                              Tải JSON
                            </Button>
                        </Box>
                      </ListItem>

                    </List>
                  )}
                </>
              )}

              {/* TAB 2: IMPORT (CHỈ HIỆN KHI KHÁC REPORT VÀ ĐANG CHỌN TAB IMPORT) */}
              {activeTab === 'import' && mode !== 'report' && (
                  <Box sx={{ mt: 2, mb: 4 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                       Bạn có thể nhập dữ liệu hàng loạt thông qua tệp Excel (.xlsx) hoặc JSON (.json). Vui lòng đảm bảo cấu trúc tệp đúng định dạng quy định của hệ thống.
                    </Typography>
                    
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa', border: '1px dashed #ccc', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Button 
                          variant="outlined" color="primary" size="medium" sx={{ textTransform: 'none', borderRadius: 2, justifyContent: 'flex-start', px: 2 }} 
                          disabled={isProcessing} startIcon={isProcessing ? <CircularProgress size={16} /> : <FileUploadIcon />} onClick={() => excelFileInputRef.current.click()}
                        >
                          Tải lên tệp Excel (.xlsx)
                        </Button>
                        <Button 
                          variant="outlined" color="info" size="medium" sx={{ textTransform: 'none', borderRadius: 2, justifyContent: 'flex-start', px: 2 }} 
                          disabled={isProcessing} startIcon={isProcessing ? <CircularProgress size={16} /> : <CodeIcon />} onClick={() => jsonFileInputRef.current.click()}
                        >
                          Tải lên tệp JSON (.json)
                        </Button>
                    </Paper>
                  </Box>
              )}

            </Box>
        </Box>
      </ManagedModal>

      {/* FORM THÊM/SỬA MẪU IN — field dựng từ cấu hình listinfo `rpt` */}
      {templateForm.open && (
        <DataListForm
          open={templateForm.open}
          onClose={() => setTemplateForm({ open: false, editing: false, data: null })}
          isEditing={templateForm.editing}
          initialData={templateForm.data}
          fields={rptFormFields}
          saving={savingTemplate}
          onSave={luuMau}
          formSize="lg"
        />
      )}

      {/* MENU CHỌN LIÊN IN */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} PaperProps={{ sx: { borderRadius: 3, mt: 1, minWidth: 200, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' } }}>
         <MenuItem disabled sx={{ opacity: '1 !important', fontWeight: 'bold', color: 'primary.main', bgcolor: '#f5f5f5', pb: 1.5 }}>Vui lòng chọn liên:</MenuItem>
         {selectedTemplate?.danhSachLien?.map(lien => (
           <MenuItem key={lien.ma_lien} onClick={() => executePrint(selectedTemplate.template, printTypeAction, lien.ma_lien)} sx={{ py: 1.5, borderBottom: '1px solid #f0f0f0' }}>{lien.ten_lien}</MenuItem>
         ))}
      </Menu>
    </>
  );
}